import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UserCheck, Phone, ArrowRight, RotateCcw, Calendar as CalendarIcon } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/** IST-aware target: today before 12:00, tomorrow from 12:00 onwards. */
function defaultTargetDate() {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", hour12: false,
  });
  const parts = Object.fromEntries(fmt.formatToParts(new Date()).map((p) => [p.type, p.value]));
  const istHour = Number(parts.hour);
  const todayIst = `${parts.year}-${parts.month}-${parts.day}`;
  if (istHour < 12) return { dateStr: todayIst, isTomorrow: false };
  const [y, m, d] = todayIst.split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, d) + 86400000);
  const dateStr = `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, "0")}-${String(t.getUTCDate()).padStart(2, "0")}`;
  return { dateStr, isTomorrow: true };
}

export default function GurujiDashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [{ dateStr, isTomorrow }] = useState(defaultTargetDate);
  const [summary, setSummary] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const [s, u] = await Promise.all([
        api.get(`/bookings/today-summary?date=${dateStr}`),
        api.get("/bookings?upcoming=true"),
      ]);
      setSummary(s.data);
      setUpcoming(u.data);
    } finally {
      setRefreshing(false);
    }
  }, [dateStr]);

  useEffect(() => { load(); }, [load]);

  const dayLabel = isTomorrow ? t("admin.tomorrow") : t("admin.today");
  const friendly = new Date(`${dateStr}T00:00:00+05:30`).toLocaleDateString("en-IN", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });

  // Group upcoming by date (excluding today/tomorrow target).
  const futureByDate = useMemo(() => {
    const map = {};
    for (const b of upcoming) {
      if (b.booking_date === dateStr) continue;
      (map[b.booking_date] ||= []).push(b);
    }
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b)).slice(0, 5);
  }, [upcoming, dateStr]);

  return (
    <div data-testid="guruji-dashboard">
      {/* Welcome */}
      <div className="mb-6">
        <h1 className="font-heading text-3xl md:text-4xl">{t("admin.namaste")}, {user?.name?.split(" ")[0] || "Acharya"}-ji</h1>
        <p className="text-sm opacity-65 mt-1">{friendly}</p>
      </div>

      {/* Big Today/Tomorrow card */}
      <Link
        to="/admin/quick-attendance"
        data-testid="open-today-card"
        className="block bg-white border-2 border-[color:var(--brand)] rounded-md p-6 md:p-8 hover:shadow-lg transition-shadow"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="text-overline mb-2">{dayLabel}</div>
            <div className="flex items-baseline gap-4">
              <span className="font-heading font-bold text-6xl md:text-7xl text-[color:var(--brand)] leading-none" data-testid="dash-total">
                {summary?.total ?? "—"}
              </span>
              <span className="font-heading text-xl md:text-2xl opacity-70">
                {summary?.total === 1 ? t("admin.bookingSingular") : t("admin.bookingPlural")}
              </span>
            </div>
            <p className="text-sm opacity-65 mt-3">{t("admin.tapToOpen")}</p>
          </div>
          <div className="flex items-center gap-2 text-[color:var(--brand)]">
            <UserCheck size={20} />
            <span className="font-medium text-sm">{t("admin.markAttendance")}</span>
            <ArrowRight size={16} />
          </div>
        </div>

        {/* Per-pooja breakdown */}
        {summary && Object.keys(summary.by_pooja).length > 0 && (
          <div className="mt-6 pt-6 border-t border-dashed border-[color:var(--border)] grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {Object.entries(summary.by_pooja).map(([slug, info]) => (
              <div key={slug} className="flex items-baseline justify-between gap-3 px-4 py-3 rounded-md bg-[color:var(--bg-2)]" data-testid={`dash-pooja-${slug}`}>
                <span className="text-sm font-medium truncate">{info.pooja_name}</span>
                <span className="font-heading font-bold text-2xl text-[color:var(--brand)]">{info.count}</span>
              </div>
            ))}
          </div>
        )}
      </Link>

      {/* Refresh */}
      <div className="mt-4 flex items-center gap-3">
        <button onClick={load} disabled={refreshing} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[color:var(--border)] bg-white text-sm" data-testid="dash-refresh">
          <RotateCcw size={14} className={refreshing ? "animate-spin" : ""} /> {t("admin.refresh")}
        </button>
      </div>

      {/* Upcoming days preview */}
      {futureByDate.length > 0 && (
        <section className="mt-10">
          <div className="flex items-center gap-2 mb-4">
            <CalendarIcon size={16} className="text-[color:var(--brand)]" />
            <h2 className="font-heading text-2xl">{t("admin.upcomingDays")}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {futureByDate.map(([date, list]) => (
              <div key={date} className="bg-white border border-[color:var(--border)] rounded-md p-4" data-testid={`upcoming-day-${date}`}>
                <div className="flex items-baseline justify-between">
                  <div className="font-heading text-lg">
                    {new Date(`${date}T00:00:00+05:30`).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })}
                  </div>
                  <div className="text-[color:var(--brand)] font-heading font-bold text-2xl">{list.length}</div>
                </div>
                <ul className="mt-3 space-y-1.5 text-xs">
                  {list.slice(0, 3).map((b) => (
                    <li key={b.id} className="flex items-center justify-between">
                      <span className="truncate font-medium">{b.devotee_name}</span>
                      <a href={`tel:${b.mobile}`} className="text-[color:var(--brand)] inline-flex items-center gap-1 flex-shrink-0 ml-2">
                        <Phone size={10} />
                      </a>
                    </li>
                  ))}
                  {list.length > 3 && <li className="opacity-60 text-[11px]">+ {list.length - 3} more</li>}
                </ul>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
